import axios, { AxiosResponse } from "axios";
import { ActionContext, Module } from "vuex";
import {
  KahiListItemData,
  KahiDetailData,
  KahiDetailRelationLinkData,
  KahiEasySearch,
  KahiEasySearchAPIResponse,
  KahiLatLng,
  KahiMarker,
  KahiDetailApiResponse,
  KahisApiResponse,
  Kodo,
  KodosApiResponse,
  KahiDetailRelationData,
  KahiDetailApiResponseRelationData
} from "@/@types";
import { RootState } from "@/store/";

export interface KahiState {
  currentDirection: number | null;
  currentPosition: KahiLatLng | null;
  currentPositionWatchID: number | null;
  disableGeolocation: boolean;
  easySearchs: KahiEasySearch[];
  kahis: KahiListItemData[];
  kodos: Kodo[];
  loadEasySearchsPromise: Promise<void> | null;
  loadKahisPromise: Promise<void> | null;
  loadKodosPromise: Promise<void> | null;
  markers: KahiMarker[];
  searchMatched: string[];
  selected: KahiListItemData | null;
}

function calcurateDistance(
  state: KahiState,
  coords: KahiLatLng | null
): number | null {
  if (
    state.disableGeolocation ||
    state.currentPosition === null ||
    coords === null
  ) {
    return null;
  } else {
    // 地球楕円体（WGS84）
    // a: 赤道半径
    // b: 極半径
    const a = 6378137.0;
    const b = 6356752.314245;

    // ラジアンに変換
    const rad = (deg: number) => {
      return (deg * Math.PI) / 180;
    };

    const lat1: number = rad(coords.lat);
    const lng1: number = rad(coords.lng);
    const lat2: number = rad(state.currentPosition.lat);
    const lng2: number = rad(state.currentPosition.lng);

    // 2点間の緯度・経度の差、平均
    const latDiff: number = lat1 - lat2;
    const lngDiff: number = lng1 - lng2;
    const latAvg: number = (lat1 + lat2) / 2.0;

    // 離心率の2乗
    const E2 = (a * a - b * b) / (a * a);

    // 子午線・卯酉線曲率半径の分母
    const sinLatAvg = Math.sin(latAvg);
    const W = Math.sqrt(1.0 - E2 * sinLatAvg * sinLatAvg);

    // 子午線曲率半径
    const M = (a * (1.0 - E2)) / (W * W * W);

    // 卯酉線曲率半径
    const N = a / W;

    // 2点間の距離
    const t1 = latDiff * M;
    const t2 = lngDiff * N * Math.cos(latAvg);
    const d = Math.sqrt(t1 * t1 + t2 * t2);

    return d;
  }
}

export const KahiStore: Module<KahiState, RootState> = {
  namespaced: true,
  state: {
    currentDirection: null,
    currentPosition: null,
    currentPositionWatchID: null,
    disableGeolocation: true,
    easySearchs: [],
    kahis: [],
    kodos: [],
    loadEasySearchsPromise: null,
    loadKahisPromise: null,
    loadKodosPromise: null,
    markers: [],
    searchMatched: [],
    selected: null
  },
  mutations: {
    /**
     * 現在の方角を設定
     *
     * @param  { KahiState } state
     * @param  { number | null } currentDirection
     * @return { void }
     */
    setCurrentDirection(
      state: KahiState,
      currentDirection: number | null
    ): void {
      state.currentDirection = currentDirection;
    },
    /**
     * 現在位置を設定
     *
     * @param  { KahiState } state
     * @param  { KahiLatLng | null } currentPosition
     * @return { void }
     */
    setCurrentPosition(
      state: KahiState,
      currentPosition: KahiLatLng | null
    ): void {
      state.currentPosition = currentPosition;
    },
    /**
     * navigator.geolocation.watchPositionのハンドラーIDを設定
     *
     * @param  { KahiState } state
     * @param  { number | null } currentPositionWatchID
     * @return { void }
     */
    setCurrentPositionWatchID(
      state: KahiState,
      currentPositionWatchID: number | null
    ): void {
      state.currentPositionWatchID = currentPositionWatchID;
    },
    /**
     * Geolocation APIの利用の可否を設定
     *
     * @param  { KahiState } state
     * @param  { boolean } disableGeolocation
     * @return { void }
     */
    setDisableGeolocation(state: KahiState, disableGeolocation: boolean): void {
      state.disableGeolocation = disableGeolocation;
    },
    /**
     * かんたん検索リストを設定
     *
     * @param  { KahiState } state
     * @param  { KahiEasySearchAPIResponse[][] } data
     * @return { void }
     */
    setEasySearchs(
      state: KahiState,
      data: KahiEasySearchAPIResponse[][]
    ): void {
      const easySearchs: KahiEasySearch[] = [];

      for (const easySearchGroup of data) {
        for (const easySearch of easySearchGroup) {
          easySearchs.push({
            label: easySearch.label,
            and: easySearch.and,
            conditions: easySearch.cond
          });
        }
      }

      state.easySearchs = easySearchs;
    },
    /**
     * 歌碑リスト表示用データを設定
     *
     * @param  { KahiState } state
     * @param  { KahisApiResponse[] } data
     * @return { void }
     */
    setKahis(state: KahiState, data: KahisApiResponse[]): void {
      const kahis: KahiListItemData[] = [];

      for (const d of data) {
        const coords: KahiLatLng = {
          lat: d.lat,
          lng: d.long
        };

        kahis.push({
          Area: d.Area,
          KID: d.KID,
          coords: coords,
          distance: calcurateDistance(state, coords),
          image: d.image + "?thumbnail",
          kigou: d.kigou,
          kno1: d.kno1,
          saku1: d.saku1,
          saku2: d.saku2,
          uta1: d.uta1,
          uta2: d.uta2,
          volume: d.volume
        });
      }

      state.kahis = kahis;
    },
    /**
     * 古道データを設定
     *
     * @param  { KahiState } state
     * @param  { KodosApiResponse } data
     * @return { void }
     */
    setKodos(state: KahiState, data: KodosApiResponse): void {
      state.kodos = data.kodo;
    },
    /**
     * かんたん検索多重読込防止用Promiseを設定
     *
     * @param  { KahiState } state
     * @param  { Promise<void> } promise
     * @return { void }
     */
    setLoadEasySearchsPromise(state: KahiState, promise: Promise<void>): void {
      state.loadEasySearchsPromise = promise;
    },
    /**
     * 歌碑リスト多重読込防止用Promiseを設定
     *
     * @param  { KahiState } state
     * @param  { Promise<void> } promise
     * @return { void }
     */
    setLoadKahisPromise(state: KahiState, promise: Promise<void>): void {
      state.loadKahisPromise = promise;
    },
    /**
     * 古道リスト多重読込防止用Promiseを設定
     *
     * @param  { KahiState } state
     * @param  { Promise<void> } promise
     * @return { void }
     */
    setLoadKodosPromise(state: KahiState, promise: Promise<void>): void {
      state.loadKodosPromise = promise;
    },
    /**
     * 歌碑マーカー表示用データを設定
     *
     * @param  { KahiState } state
     * @param  { KahisApiResponse[] } data
     * @return { void }
     */
    setMarkers(state: KahiState, data: KahisApiResponse[]): void {
      const markers: KahiMarker[] = [];

      for (const d of data) {
        const coords: KahiLatLng = {
          lat: d.lat,
          lng: d.long
        };

        markers.push({
          KID: d.KID,
          coords: coords,
          selected: false,
          matched: false
        });
      }

      state.markers = markers;
    },
    /**
     * 検索条件にマッチした歌碑IDリストを設定
     *
     * @param  { KahiState } state
     * @param  { string[] } searchMatched
     * @return { void }
     */
    setSearchMatched(state: KahiState, searchMatched: string[]): void {
      state.searchMatched = searchMatched;
    },
    /**
     * 選択中の歌碑を設定
     *
     * @param  { KahiState } state
     * @param  { string | null } id
     * @return { void }
     */
    setSelected(state: KahiState, id: string | null): void {
      let selected: KahiListItemData | undefined;

      if (id !== null) {
        selected = state.kahis.find(kahi => {
          return kahi.KID === id;
        });
      }

      state.selected = selected ? selected : null;
    },
    /**
     * 歌碑リスト表示データ（距離）を更新
     *
     * @param  { KahiState } state
     * @return { void }
     */
    updateDistance(state: KahiState): void {
      state.kahis = state.kahis.map(kahi => {
        kahi.distance = calcurateDistance(state, kahi.coords);
        return kahi;
      });
    },
    /**
     * 歌碑マーカーステータス更新
     *
     * @param  { KahiState } state
     * @return { void }
     */
    updateMarkers(state: KahiState): void {
      state.markers = state.markers.map(marker => {
        marker.matched =
          state.searchMatched.indexOf(marker.KID) !== -1 ? true : false;
        marker.selected =
          state.selected && state.selected.KID === marker.KID ? true : false;
        return marker;
      });

      state.markers.sort((a: KahiMarker, b: KahiMarker) => {
        if (a.selected || b.selected) {
          if (a.selected !== b.selected) {
            if (a.selected) {
              return 1;
            } else {
              return -1;
            }
          }
        } else if (a.matched || b.matched) {
          if (a.matched !== b.matched) {
            if (a.matched) {
              return 1;
            } else {
              return -1;
            }
          }
        }

        return 0;
      });
    }
  },
  actions: {
    /**
     * 現在位置と指定した座標の距離を計算
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @param  { KahiLatLng } payload
     * @return { Promise<number | null> }
     */
    async calcurateDistance(
      context: ActionContext<KahiState, RootState>,
      payload: KahiLatLng
    ): Promise<number | null> {
      return new Promise<number | null>(resolve => {
        resolve(calcurateDistance(context.state, payload));
      });
    },
    /**
     * 現在位置を取得
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @return { void }
     */
    getCurrentPosition(context: ActionContext<KahiState, RootState>): void {
      if (navigator.geolocation) {
        if (
          context.state.currentPositionWatchID !== null ||
          !context.state.disableGeolocation
        ) {
          return;
        }

        const successCallback: PositionCallback = position => {
          context.commit("setCurrentDirection", position.coords.heading);
          context.commit("setCurrentPosition", {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          context.commit("setDisableGeolocation", false);
          context.commit("updateDistance");
        };

        const errorCallback: PositionErrorCallback = error => {
          console.log(error);
          context.commit("setCurrentDirection", null);
          context.commit("setCurrentPosition", null);
          context.commit("setDisableGeolocation", true);
          context.commit("updateDistance");
        };

        const options: PositionOptions = {
          timeout: 10000
        };

        const watchID = navigator.geolocation.watchPosition(
          successCallback,
          errorCallback,
          options
        );

        context.commit("setCurrentPositionWatchID", watchID);
      } else {
        context.commit("setCurrentDirection", null);
        context.commit("setCurrentPosition", null);
        context.commit("setDisableGeolocation", true);
        context.commit("updateDistance");
      }
    },
    /**
     * かんたん検索リストを取得
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @return { Promise<Promise<void> | null> }
     */
    async getEasySeatchs(
      context: ActionContext<KahiState, RootState>
    ): Promise<Promise<void> | null> {
      if (context.state.loadEasySearchsPromise !== null) {
        return context.state.loadEasySearchsPromise;
      }

      context.commit(
        "setLoadEasySearchsPromise",
        new Promise<void>((resolve, reject) => {
          axios
            .get<KahiEasySearchAPIResponse[][]>("/api/kahi/easy/")
            .then((response: AxiosResponse<KahiEasySearchAPIResponse[][]>) => {
              context.commit("setEasySearchs", response.data);
              resolve();
            })
            .catch(() => {
              reject();
            });
        })
      );

      return context.state.loadEasySearchsPromise;
    },
    /**
     * 歌碑詳細情報を取得
     *
     * @param { ActionContext<KahiState, RootState> } _
     * @param { string } payload
     * @return { Promise<Promise<KahiDetailData> }
     */
    async getKahi(
      _: ActionContext<KahiState, RootState>,
      payload: string
    ): Promise<KahiDetailData> {
      return new Promise<KahiDetailData>((resolve, reject) => {
        axios
          .get<KahiDetailApiResponse>(`/api/kahi/detail/${payload}`)
          .then((response: AxiosResponse<KahiDetailApiResponse>) => {
            const coords: KahiLatLng = {
              lat: response.data.lat,
              lng: response.data.long
            };

            let relation: KahiDetailRelationData | null = null;

            if (response.data.relation !== null) {
              const r: KahiDetailApiResponseRelationData =
                response.data.relation;
              const links: KahiDetailRelationLinkData[] = [];

              for (const link of r.links) {
                links.push({
                  KID: link.KID,
                  coords: {
                    lat: link.lat,
                    lng: link.long
                  }
                });
              }

              relation = {
                kno2: r.kno2,
                saku2: r.saku2,
                uta2: r.uta2,
                volume: r.volume,
                links: links
              };
            }

            resolve({
              KID: response.data.KID,
              coords: coords,
              image: response.data.image,
              kaisetsu: response.data.kaisetsu,
              kigou: response.data.kigou,
              kno1: response.data.kno1,
              relation: relation,
              saku1: response.data.saku1,
              shozaichi: response.data.shozaichi,
              uta1: response.data.uta1,
              volume: response.data.volume
            });
          })
          .catch(() => {
            reject();
          });
      });
    },
    /**
     * 歌碑リストを取得
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @return { Promise<Promise<void> | null> }
     */
    async getKahis(
      context: ActionContext<KahiState, RootState>
    ): Promise<Promise<void> | null> {
      if (context.state.loadKahisPromise !== null) {
        return context.state.loadKahisPromise;
      }

      context.commit(
        "setLoadKahisPromise",
        new Promise<void>((resolve, reject) => {
          axios
            .get<KahisApiResponse[]>("/api/kahi/")
            .then((response: AxiosResponse<KahisApiResponse[]>) => {
              context.commit("setKahis", response.data);
              context.commit("setMarkers", response.data);
              resolve();
            })
            .catch(() => {
              reject();
            });
        })
      );

      return context.state.loadKahisPromise;
    },
    /**
     * 古道リストを取得
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @return { Promise<Promise<void> | null> }
     */
    async getKodos(
      context: ActionContext<KahiState, RootState>
    ): Promise<Promise<void> | null> {
      if (context.state.loadKodosPromise !== null) {
        return context.state.loadKodosPromise;
      }

      context.commit(
        "setLoadKodosPromise",
        new Promise<void>((resolve, reject) => {
          axios
            .get<KodosApiResponse>("/api/kodo/")
            .then((response: AxiosResponse<KodosApiResponse>) => {
              context.commit("setKodos", response.data);
              resolve();
            })
            .catch(() => {
              reject();
            });
        })
      );

      return context.state.loadKodosPromise;
    },
    /**
     * 検索条件に一致した歌碑マーカーを選択
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @param  { string[] } payload
     * @return { void }
     */
    setFilteredMarker(
      context: ActionContext<KahiState, RootState>,
      payload: string[]
    ): void {
      context.commit("setSearchMatched", payload);
      context.commit("updateMarkers");
    },
    /**
     * 歌碑マーカーを選択
     *
     * @param  { ActionContext<KahiState, RootState> } context
     * @param  { string | null } payload
     * @return { void }
     */
    selectMarker(
      context: ActionContext<KahiState, RootState>,
      payload: string | null
    ): void {
      context.commit("setSelected", payload);
      context.commit("updateMarkers");
    }
  }
};
